<template>
  <section class="page-shell audit-page">
    <div class="page-heading">
      <div>
        <h1>Auditoria</h1>
        <p>Histórico de ações importantes do workspace em linguagem simples.</p>
      </div>
      <v-btn outlined color="primary" :loading="loading" @click="load(pagination.page)">Atualizar</v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

    <section class="data-panel audit-panel">
      <div class="audit-panel-header">
        <div>
          <strong>Eventos recentes</strong>
          <span>{{ paginationSummary }}</span>
        </div>
      </div>

      <v-simple-table class="audit-table">
        <thead>
          <tr>
            <th>Ação</th>
            <th>Categoria</th>
            <th>Alvo</th>
            <th>Responsável</th>
            <th>Quando</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="event in events" :key="event.id">
            <td data-label="Ação">
              <div class="audit-event-cell">
                <v-icon :color="eventIcon(event).color" size="20">{{ eventIcon(event).icon }}</v-icon>
                <div>
                  <strong>{{ eventTitle(event) }}</strong>
                  <span>{{ eventDescription(event) }}</span>
                </div>
              </div>
            </td>
            <td data-label="Categoria">
              <v-chip small :color="levelInfo(event.event_level).color" :text-color="levelInfo(event.event_level).textColor">
                {{ levelInfo(event.event_level).label }}
              </v-chip>
            </td>
            <td data-label="Alvo">
              <div class="audit-muted-cell">
                <strong>{{ entityLabel(event) }}</strong>
                <span v-if="event.entity_id">ID {{ event.entity_id }}</span>
              </div>
            </td>
            <td data-label="Responsável">
              <div class="audit-muted-cell">
                <strong>{{ userLabel(event) }}</strong>
              </div>
            </td>
            <td data-label="Quando">
              <div class="audit-muted-cell">
                <strong>{{ formatDate(event.created_at) }}</strong>
                <span>{{ formatRelativeDate(event.created_at) }}</span>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && !events.length">
            <td colspan="5" class="empty-cell">Nenhum evento encontrado.</td>
          </tr>
        </tbody>
      </v-simple-table>

      <div v-if="pagination.lastPage > 1" class="audit-pagination">
        <span>Página {{ pagination.page }} de {{ pagination.lastPage }}</span>
        <v-pagination
          v-model="pagination.page"
          :length="pagination.lastPage"
          :total-visible="7"
          @input="load"
        />
      </div>
    </section>
  </section>
</template>

<script>
import api from '../../plugins/axios'
import { billingErrorMessage } from '../../utils/apiErrors'

const operationalEventTypes = [
  'ai_document_validation.validated',
  'ai_tool.archived',
  'ai_tool.created',
  'ai_tool.updated',
  'ai_use_request.approved',
  'ai_use_request.created',
  'ai_use_request.rejected',
  'ai_use_request.submitted',
  'billing.payment_method_configured',
  'billing.reconciliation_fixed',
  'document_generation.created',
  'evidence.generated',
  'employee.access_granted',
  'employee.access_removed',
  'employee.access_updated',
  'generated_document.created',
  'generated_document.deleted',
  'generated_document.ai_adjusted',
  'generated_document.updated',
  'governance_policy.created',
  'governance_policy.deleted',
  'governance_policy.updated',
  'human_document_review.approved',
  'human_document_review.created',
  'human_document_review.rejected',
  'human_document_review.submitted',
  'judicial_metadata.datajud_queried',
  'notification.email_failed',
  'safe_ai_use.evaluated'
]

const eventDictionary = {
  'judicial_metadata.datajud_queried': {
    title: 'Consulta DataJud realizada',
    description: 'Metadados públicos de um processo foram consultados no CNJ.',
    icon: 'mdi-database-search-outline',
    color: 'primary'
  },
  'safe_ai_use.evaluated': {
    title: 'Uso de IA avaliado',
    description: 'Uma solicitação passou pela análise de segurança de IA.',
    icon: 'mdi-shield-check-outline',
    color: 'success'
  },
  'ai_document_validation.validated': {
    title: 'Documento validado por IA',
    description: 'Um documento foi analisado pelas regras de validação.',
    icon: 'mdi-file-check-outline',
    color: 'success'
  },
  'document_generation.created': {
    title: 'Documento gerado',
    description: 'Uma nova minuta foi criada com apoio da IA.',
    icon: 'mdi-file-document-edit-outline',
    color: 'primary'
  },
  'generated_document.created': {
    title: 'Documento salvo',
    description: 'Um documento foi registrado no workspace.',
    icon: 'mdi-file-plus-outline',
    color: 'primary'
  },
  'generated_document.updated': {
    title: 'Documento atualizado',
    description: 'Um documento gerado recebeu alterações.',
    icon: 'mdi-file-sync-outline',
    color: 'primary'
  },
  'generated_document.ai_adjusted': {
    title: 'Documento ajustado por IA',
    description: 'Um documento recebeu ajuste textual assistido por IA.',
    icon: 'mdi-auto-fix',
    color: 'primary'
  },
  'generated_document.deleted': {
    title: 'Documento removido',
    description: 'Um documento foi removido do workspace.',
    icon: 'mdi-file-remove-outline',
    color: 'warning'
  },
  'ai_tool.created': {
    title: 'Ferramenta de IA criada',
    description: 'Uma ferramenta permitida para uso de IA foi cadastrada.',
    icon: 'mdi-robot-outline',
    color: 'primary'
  },
  'ai_tool.updated': {
    title: 'Ferramenta de IA atualizada',
    description: 'Uma regra de ferramenta de IA recebeu alterações.',
    icon: 'mdi-robot-outline',
    color: 'primary'
  },
  'ai_tool.archived': {
    title: 'Ferramenta de IA arquivada',
    description: 'Uma ferramenta de IA foi retirada do uso ativo.',
    icon: 'mdi-archive-outline',
    color: 'warning'
  },
  'governance_policy.created': {
    title: 'Política criada',
    description: 'Uma nova regra de governança foi cadastrada.',
    icon: 'mdi-clipboard-plus-outline',
    color: 'primary'
  },
  'governance_policy.updated': {
    title: 'Política atualizada',
    description: 'Uma regra de governança recebeu alterações.',
    icon: 'mdi-clipboard-edit-outline',
    color: 'primary'
  },
  'governance_policy.deleted': {
    title: 'Política removida',
    description: 'Uma regra de governança foi excluída.',
    icon: 'mdi-clipboard-remove-outline',
    color: 'warning'
  },
  'ai_use_request.created': {
    title: 'Solicitação de IA criada',
    description: 'Uma solicitação de governança de uso de IA foi aberta.',
    icon: 'mdi-account-clock-outline',
    color: 'primary'
  },
  'ai_use_request.submitted': {
    title: 'Solicitação de IA enviada',
    description: 'Uma solicitação de governança foi enviada para decisão.',
    icon: 'mdi-send-outline',
    color: 'primary'
  },
  'ai_use_request.approved': {
    title: 'Uso aprovado',
    description: 'Uma solicitação de uso foi aprovada.',
    icon: 'mdi-check-decagram-outline',
    color: 'success'
  },
  'ai_use_request.rejected': {
    title: 'Uso recusado',
    description: 'Uma solicitação de uso foi recusada.',
    icon: 'mdi-close-octagon-outline',
    color: 'error'
  },
  'human_document_review.created': {
    title: 'Revisão documental criada',
    description: 'Um documento foi aberto para revisão humana.',
    icon: 'mdi-file-document-edit-outline',
    color: 'primary'
  },
  'human_document_review.submitted': {
    title: 'Documento enviado para revisão',
    description: 'Um documento entrou na fila de decisão humana.',
    icon: 'mdi-send-check-outline',
    color: 'primary'
  },
  'human_document_review.approved': {
    title: 'Documento aprovado',
    description: 'Uma revisão documental foi liberada por responsável humano.',
    icon: 'mdi-file-check-outline',
    color: 'success'
  },
  'human_document_review.rejected': {
    title: 'Documento bloqueado',
    description: 'Uma revisão documental foi recusada por responsável humano.',
    icon: 'mdi-file-cancel-outline',
    color: 'error'
  },
  'employee.access_granted': {
    title: 'Acesso concedido',
    description: 'Uma pessoa recebeu acesso ao workspace.',
    icon: 'mdi-account-key-outline',
    color: 'success'
  },
  'employee.access_updated': {
    title: 'Acesso atualizado',
    description: 'As permissões de uma pessoa foram alteradas.',
    icon: 'mdi-account-edit-outline',
    color: 'primary'
  },
  'employee.access_removed': {
    title: 'Acesso removido',
    description: 'Uma pessoa perdeu acesso ao workspace.',
    icon: 'mdi-account-remove-outline',
    color: 'warning'
  },
  'evidence.generated': {
    title: 'Evidência gerada',
    description: 'Uma evidência de auditoria foi registrada.',
    icon: 'mdi-file-search-outline',
    color: 'primary'
  },
  'billing.payment_method_configured': {
    title: 'Pagamento configurado',
    description: 'O método de pagamento da conta foi atualizado.',
    icon: 'mdi-credit-card-check-outline',
    color: 'success'
  },
  'billing.reconciliation_fixed': {
    title: 'Cobrança conciliada',
    description: 'Uma divergência de cobrança foi corrigida.',
    icon: 'mdi-cash-sync',
    color: 'success'
  },
  'notification.email_failed': {
    title: 'E-mail não enviado',
    description: 'Uma notificação por e-mail falhou.',
    icon: 'mdi-email-alert-outline',
    color: 'warning'
  }
}

const levelDictionary = {
  security: { label: 'Segurança', color: '#e9f7ef', textColor: '#137333' },
  compliance: { label: 'Compliance', color: '#eaf1ff', textColor: '#0f4efb' },
  billing: { label: 'Conta e cobrança', color: '#fff6db', textColor: '#815800' }
}

const entityDictionary = {
  ai_use_request: 'Solicitação de uso de IA',
  human_document_review: 'Revisão documental',
  ai_tool: 'Ferramenta de IA',
  generated_document: 'Documento gerado',
  governance_policy: 'Política de governança',
  judicial_metadata_query: 'Consulta DataJud',
  judicial_metadata_batch: 'Lote DataJud',
  evidence: 'Evidência',
  employee: 'Usuário interno'
}

export default {
  name: 'AuditPage',
  data () {
    return {
      loading: false,
      error: '',
      events: [],
      pagination: {
        page: 1,
        perPage: 15,
        total: 0,
        lastPage: 1
      }
    }
  },
  computed: {
    paginationSummary () {
      if (!this.pagination.total) return 'Nenhum registro encontrado'

      const first = ((this.pagination.page - 1) * this.pagination.perPage) + 1
      const last = Math.min(this.pagination.page * this.pagination.perPage, this.pagination.total)

      return `Mostrando ${first}-${last} de ${this.pagination.total} registro(s)`
    }
  },
  created () {
    this.load(1)
  },
  methods: {
    async load (page = 1) {
      this.loading = true
      this.error = ''
      try {
        const { data } = await api.get('/audit-logs', {
          params: {
            page,
            limit: this.pagination.perPage
          }
        })
        this.events = (data.data || []).filter(event => operationalEventTypes.includes(event.event_type))
        this.pagination = {
          page: Number(data.page || page),
          perPage: Number(data.perPage || this.pagination.perPage),
          total: Number(data.total || this.events.length),
          lastPage: Number(data.lastPage || 1)
        }
      } catch (error) {
        this.error = billingErrorMessage(error, 'Não foi possível carregar auditoria.')
      } finally {
        this.loading = false
      }
    },
    formatDate (value) {
      if (!value) return '-'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR')
    },
    formatRelativeDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''

      const diffMs = Date.now() - date.getTime()
      const diffMinutes = Math.floor(diffMs / 60000)
      if (diffMinutes < 1) return 'agora há pouco'
      if (diffMinutes < 60) return `há ${diffMinutes} min`

      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours < 24) return `há ${diffHours} h`

      const diffDays = Math.floor(diffHours / 24)
      if (diffDays === 1) return 'ontem'
      if (diffDays < 30) return `há ${diffDays} dias`

      return ''
    },
    eventTitle (event) {
      return (eventDictionary[event.event_type] && eventDictionary[event.event_type].title) || this.humanizeCode(event.event_type)
    },
    eventDescription (event) {
      return (eventDictionary[event.event_type] && eventDictionary[event.event_type].description) || 'Registro operacional armazenado na trilha de auditoria.'
    },
    eventIcon (event) {
      const info = eventDictionary[event.event_type] || {}
      return {
        icon: info.icon || 'mdi-clipboard-text-clock-outline',
        color: info.color || 'primary'
      }
    },
    levelInfo (level) {
      return levelDictionary[level] || { label: this.humanizeCode(level || 'operacional'), color: '#eef2f5', textColor: '#4d5965' }
    },
    entityLabel (event) {
      if (!event.entity_type) return 'Plataforma'
      return entityDictionary[event.entity_type] || this.humanizeCode(event.entity_type)
    },
    userLabel (event) {
      if (event.user) {
        return event.user.name || event.user.username || event.user.email || `Usuário #${event.user.id || event.user_id}`
      }

      return event.user_id ? `Usuário #${event.user_id}` : 'Sistema'
    },
    humanizeCode (value) {
      return String(value || '')
        .replace(/[_-]/g, ' ')
        .replace(/\./g, ' · ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, letter => letter.toUpperCase()) || '-'
    }
  }
}
</script>
